/**
 * Operational metrics — mgmt API + `ccBearerToken`, used on page load. Not the same
 * backend as ./analytics.ts. Keeps the legacy error contract; see types.ts.
 */

import constants from '../../constants'
import axiosInstance from '../axiosInstance'
import { toServiceError } from './errors'
import { MetricRangeParams, MetricsApp, MetricsBundle, MetricsServiceError } from './types'

/** Every operational call carries the dashboard JWT, read at call time. */
function authHeaders(): Record<string, string> {
  const ccBearerToken = localStorage.getItem('ccBearerToken')
  return { Authorization: `Bearer ${ccBearerToken}` }
}

/**
 * All seven page-load metrics in parallel. Each field holds the FULL response body,
 * and voice/video/recording share one range.
 */
export async function get(
  app: MetricsApp,
  mauCall: MetricRangeParams,
  peakCcuCall: MetricRangeParams,
  voiceVideoCall: MetricRangeParams,
  mcuCall: MetricRangeParams,
  dcuCall: MetricRangeParams,
): Promise<MetricsBundle | MetricsServiceError> {
  const URL = `${constants.customerDomain}apps/${app.id}/metrics/`
  const headers = authHeaders()

  const requests = [
    `${URL}${mauCall.type}/${mauCall.startRange}/${mauCall.endRange}`,
    `${URL}${peakCcuCall.type}/${peakCcuCall.startRange}/${peakCcuCall.endRange}`,
    `${URL}voice/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}video/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}recording/${voiceVideoCall.startRange}/${voiceVideoCall.endRange}`,
    `${URL}${mcuCall.type}/${mcuCall.startRange}/${mcuCall.endRange}`,
    `${URL}${dcuCall.type}/${dcuCall.startRange}/${dcuCall.endRange}`,
    // fresh config per request — the legacy service built one object each, and
    // sharing a single reference across 7 concurrent calls invites surprises
  ].map(url => axiosInstance.get(url, { headers, timeout: constants.timeout_in_ms.metrics }))

  return Promise.all(requests)
    .then(responses => ({
      mau: responses[0].data,
      peakCcu: responses[1].data,
      voice: responses[2].data,
      video: responses[3].data,
      recording: responses[4].data,
      mcu: responses[5].data,
      dcu: responses[6].data,
      metricSuccess: true,
    }))
    .catch(toServiceError)
}

export default function init(): void {}
